import React, { Component } from 'react';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import {
  browserList,
  buttonSize,
  pwaInstallUserChoices,
  approxPWAInstallTime,
  pwaStartPath,
  fallbackButtonOnGetAppDisplayTime,
  safariFallbackButtonOnGetAppDisplayTime,
} from '../../constants/globals';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import { selectRestaurant } from '../../selectors';
import { detectWebAppBrowser, isInStandaloneMode } from '../../utils/helpers';
import { constants } from '../../constants/configuration';
import './DownloadApp.scss';

const AsyncImage = asyncRoute(() => import('../../components/elements/AsyncImage'));
const InstallButton = asyncRoute(() => import('../../components/elements/Button/Install'));
const Button = asyncRoute(() => import('../../components/elements/Button'));
const SafariPWAInstallGuide = asyncRoute(() =>
  import('../../components/Popup/InstallAppetizer/SafariGuide'),
);

const pwaConfig = constants.progressiveWebApp;
// Safari PWA install related variables
const browserInfo = detectWebAppBrowser();
const isSafari = !isEmpty(browserInfo) && browserInfo['name'] === browserList.iOSSafari;

class DownloadApp extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showSafariPWAInstallPopup: false,
      getAppCTAClicked: false,
      stopInstallingAppLoader: false,
      showPWAInstallFallbackButton: true,
    };
  }

  componentDidMount() {
    // Hide fallback button after the timer expires, regardless of whether it can be detected that the app has already been installed
    setTimeout(
      () => {
        this.setState({ showPWAInstallFallbackButton: false });
      },
      isSafari ? safariFallbackButtonOnGetAppDisplayTime : fallbackButtonOnGetAppDisplayTime, // Enhance UX for safari using lower fallback display time value so that safari users don't have to wait for long to install the PWA
    );
  }

  componentDidUpdate(prevProps) {
    // On android, when the user has accepted to install the app, stop the loader after approx pwa install time.
    // The 'appinstalled' event does not fire reliably, even on the supported devices. So, this strategy is used instead, as a workaround.
    if (
      this.props.pwaInstallUserChoice === pwaInstallUserChoices.accepted &&
      this.props.pwaInstallUserChoice !== prevProps.pwaInstallUserChoice
    ) {
      setTimeout(() => {
        this.setStopInstallingAppLoader(true);
      }, approxPWAInstallTime);
    }

    // In case of android devices, hide fallback button when the parent sends prop isPWAInstallable with the value true
    if (
      this.props.isPWAInstallable !== prevProps.isPWAInstallable &&
      !!this.props.isPWAInstallable
    ) {
      this.setState({ showPWAInstallFallbackButton: false });
    }

    // In case of android devices, hide fallback button when the parent sends prop isPWAInstalled with the value true
    if (this.props.isPWAInstalled !== prevProps.isPWAInstalled && !!this.props.isPWAInstalled) {
      this.setState({ showPWAInstallFallbackButton: false });
    }
  }

  showSafariPWAInstallPopup = () => this.setState({ showSafariPWAInstallPopup: true });

  onCloseSafariPWAInstallPopup = () => this.setState({ showSafariPWAInstallPopup: false });

  setGetAppCTAClicked = (flag) => this.setState({ getAppCTAClicked: flag });

  setStopInstallingAppLoader = (flag) => this.setState({ stopInstallingAppLoader: flag });

  handleGetAppClick = (isSafari, handleInstallPWAClick) => {
    this.setGetAppCTAClicked(true);

    if (isSafari) {
      this.showSafariPWAInstallPopup();
    } else {
      handleInstallPWAClick();
    }
  };

  render() {
    const {
      showPWAInstallFallbackButton,
      showSafariPWAInstallPopup,
      getAppCTAClicked,
      stopInstallingAppLoader,
    } = this.state;

    const {
      handleInstallPWAClick,
      appStartupInfo,
      restaurant,
      restaurantGroup,
      enablePWAInstallWidgets,
      pwaInstallUserChoice,
      isPWAInstallable,
    } = this.props;

    const { appName, appIconUrl } = get(appStartupInfo, 'whiteLabelApp', {});

    // PWA install related variables
    let restaurantEntityName = '';
    let restaurantEntityImage = '';

    //PWA install button related variables
    const pwaInstallButtonCriteria = !isEmpty(appStartupInfo);
    let showPWAInstallButton =
      pwaInstallButtonCriteria &&
      enablePWAInstallWidgets &&
      isPWAInstallable &&
      !showPWAInstallFallbackButton;
    let showOpenPWAButton =
      pwaInstallButtonCriteria && !enablePWAInstallWidgets && !showPWAInstallFallbackButton;

    if (isSafari && !isInStandaloneMode()) {
      showPWAInstallButton = pwaInstallButtonCriteria && !showPWAInstallFallbackButton;
    }

    const isRestaurantGroupScope = get(appStartupInfo, 'subdomainObj.isGroup', false);

    // Use app name if has been provided by brand admin, else use restaurant's/group's name
    restaurantEntityName = !isEmpty(appName)
      ? appName
      : isRestaurantGroupScope
      ? get(restaurantGroup, 'name', '')
      : get(restaurant, 'name', '');

    // Use app image if has been provided by brand admin, else use restaurant's/group's image
    restaurantEntityImage = !isEmpty(appIconUrl)
      ? appIconUrl
      : isRestaurantGroupScope
      ? ''
      : get(restaurant, 'restaurantConfig.logoUrl', '');

    const showInstallingAppLoaderFlag =
      !isSafari &&
      getAppCTAClicked &&
      pwaInstallUserChoice === pwaInstallUserChoices.accepted &&
      !stopInstallingAppLoader;

    return (
      <div className="download-app">
        <div className="download-app_content">
          <div className="download-app_content_app-icon">
            <AsyncImage altText={restaurantEntityName} sourceUrl={restaurantEntityImage} />
          </div>
          <div className="download-app_content_app-name">{restaurantEntityName}</div>
          <div className="download-app_content_open-app-CTA">
            {/* Until PWA is installable, show GET APPetizer button which would do nothing */}
            {showPWAInstallFallbackButton && (
              <div className="fallback-install-button">
                <Button content={pwaConfig.install.preparingAppText} />
              </div>
            )}
            {showPWAInstallButton && (
              <InstallButton
                size={buttonSize.long}
                handleClick={() => this.handleGetAppClick(isSafari, handleInstallPWAClick)}
              />
            )}
            {showOpenPWAButton && (
              <OpenAppButton
                pwaStartPath={pwaStartPath}
                isSafari={isSafari}
                showInstallingAppLoader={showInstallingAppLoaderFlag}
              />
            )}
          </div>
          <div className="download-app_content_app-description">
            <FormattedMessage
              id="pwa-install-description"
              defaultMessage={pwaConfig.install.description}
              values={{
                restaurantEntityName,
              }}
            />
          </div>
        </div>
        {showSafariPWAInstallPopup && (
          <SafariPWAInstallGuide
            openSegmentPopup={showSafariPWAInstallPopup}
            dismissSegmentPopup={() => this.onCloseSafariPWAInstallPopup()}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  restaurant: selectRestaurant(state),
  restaurantGroup: get(state, 'app.restaurantGroup', {}),
  appStartupInfo: get(state, 'common.appStartupInfo', {}),
  appSettings: get(state, 'app.masterData.appSettings', {}),
});

export default connect(mapStateToProps, null)(DownloadApp);

const OpenAppButton = ({ pwaStartPath, isSafari, showInstallingAppLoader }) => {
  // In case of Safari, show Open App CTA directly without a loader
  // In case of Android, show loader on the CTA, and then, upon app install completion, show Open App CTA
  return (
    <div className="open-app-wrapper">
      {isSafari ? (
        // eslint-disable-next-line
        <a target="_blank" href={pwaStartPath}>
          {pwaConfig.install.openAppText}
        </a>
      ) : !showInstallingAppLoader ? (
        // eslint-disable-next-line
        <a target="_blank" href={pwaStartPath}>
          {pwaConfig.install.openAppText}
        </a>
      ) : (
        <Button loading={showInstallingAppLoader} />
      )}
    </div>
  );
};
